import { useEffect, useState, useCallback, lazy, Suspense } from "react";
import { useParams, useNavigate } from "react-router-dom";
import API from "../api/axiosConfig";
import { getDeviceId } from "../utils/deviceId";
import { motion, AnimatePresence } from "framer-motion";
import {
  Loader2, ArrowLeft, Calendar, CalendarDays,
  Trophy, X, CheckCircle2, BarChart2, MapPin, Smartphone, Store, Clock,
  Star, Pencil, Lock, Users,
} from "lucide-react";
import { toast } from "react-toastify";

const BurgerFestEventPage  = lazy(() => import("./BurgerFestEventPage"));
const DotBurgerFestPage    = lazy(() => import("./DotBurgerFestPage"));

const MEDALS      = ["🥇", "🥈", "🥉", "4️⃣", "5️⃣"];
const RANK_LABELS = ["1er", "2do", "3er", "4to", "5to"];

const FONT_FAMILIES = {
  sans:    "system-ui, -apple-system, sans-serif",
  serif:   "Georgia, 'Times New Roman', serif",
  mono:    "'Courier New', Courier, monospace",
  display: "Impact, 'Arial Black', sans-serif",
};
const FONT_SIZES = { xl: "1.25rem", "2xl": "1.5rem", "3xl": "1.875rem", "4xl": "2.25rem", "5xl": "3rem" };

function getUserLocation() {
  return new Promise((resolve) => {
    if (!navigator.geolocation) { resolve(null); return; }
    navigator.geolocation.getCurrentPosition(
      pos => resolve({ lat: pos.coords.latitude, lng: pos.coords.longitude }),
      ()   => resolve(null),
      { timeout: 10000, maximumAge: 30000 },
    );
  });
}

const API_BASE = (process.env.REACT_APP_API_URL || "http://localhost:4000/api")
  .replace(/\/api\/?$/, "");

function imgUrl(path) {
  if (!path) return null;
  if (path.startsWith("http") || path.startsWith("data:")) return path;
  return `${API_BASE}/${path.replace(/^\//, "")}`;
}

/* ─── Indicador de votos ──────────────────────────────────────── */
function VoteProgress({ used, max }) {
  if (max === 1) return null;
  const remaining = max - used;
  const pct = max > 0 ? (used / max) * 100 : 0;
  const allUsed = used >= max;

  if (max <= 8) {
    return (
      <div className="flex items-center gap-3 flex-wrap">
        <div className="flex gap-1.5 flex-wrap">
          {Array.from({ length: max }).map((_, i) => (
            <motion.div
              key={i}
              initial={{ scaleY: 0.7, opacity: 0 }}
              animate={{ scaleY: 1, opacity: 1 }}
              transition={{ delay: i * 0.04, duration: 0.25 }}
              className={`w-5 h-7 rounded-md transition-all duration-300 ${
                i < used
                  ? "bg-gradient-to-b from-amber-400 to-amber-600 shadow-sm shadow-amber-400/40"
                  : "bg-white/5 border border-white/10 border-dashed"
              }`}
            />
          ))}
        </div>
        <span className={`text-xs font-bold shrink-0 ${allUsed ? "text-amber-400" : "text-white/40"}`}>
          {allUsed ? "¡Agotados!" : `${remaining} restante${remaining !== 1 ? "s" : ""}`}
        </span>
      </div>
    );
  }

  return (
    <div className="w-full">
      <div className="flex items-end justify-between mb-2">
        <div className="flex items-baseline gap-1 leading-none">
          <motion.span
            key={used}
            initial={{ y: -8, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            className={`text-4xl font-black tabular-nums ${allUsed ? "text-amber-400" : "text-white"}`}
          >
            {used}
          </motion.span>
          <span className="text-sm font-bold text-white/30">/{max}</span>
          <span className="text-xs text-white/30 mb-0.5 ml-0.5">usados</span>
        </div>
        <span className={`text-xs font-bold px-3 py-1 rounded-full ${
          allUsed ? "bg-amber-400/15 text-amber-400" : "bg-white/5 text-white/40"
        }`}>
          {allUsed ? "¡Todos usados!" : `${remaining} restantes`}
        </span>
      </div>
      <div className="relative h-2 bg-white/5 rounded-full overflow-hidden">
        <motion.div
          initial={{ width: 0 }}
          animate={{ width: `${pct}%` }}
          transition={{ duration: 0.7, ease: "easeOut" }}
          className="absolute inset-y-0 left-0 rounded-full bg-gradient-to-r from-amber-400 to-amber-500"
        />
        {[25, 50, 75].map(t => (
          <div key={t} className="absolute top-0 bottom-0 w-px bg-white/20" style={{ left: `${t}%` }} />
        ))}
      </div>
    </div>
  );
}

/* ─── Selector de estrellas ───────────────────────────────────── */
function StarRating({ value, onChange, disabled }) {
  const [hover, setHover] = useState(0);
  return (
    <div className="flex gap-1">
      {[1, 2, 3, 4, 5].map(n => (
        <button key={n} type="button" disabled={disabled}
          onClick={() => !disabled && onChange(n)}
          onMouseEnter={() => !disabled && setHover(n)}
          onMouseLeave={() => setHover(0)}
          className="p-0.5 transition-transform active:scale-90 disabled:cursor-default">
          <Star
            size={22}
            fill={(hover || value) >= n ? "#fbbf24" : "none"}
            stroke={(hover || value) >= n ? "#fbbf24" : "rgba(255,255,255,0.2)"}
            strokeWidth={1.5}
          />
        </button>
      ))}
    </div>
  );
}

/* ─── Tarjeta de producto ─────────────────────────────────────── */
function ProductCard({ prod, myVotes, maxVotes, onVote, onRemove, onModifyVote, results, showResults, canRemoveVote, canModifyVote, votingType, voteButtonLabel, votingClosed }) {
  const [loading,      setLoading]      = useState(false);
  const [pendingStars, setPendingStars] = useState(0);
  const voteForThis = myVotes.find(v => v.productId === prod.id);
  const isVoted     = Boolean(voteForThis);
  const usedVotes   = myVotes.length;
  const canVote     = !isVoted && usedVotes < maxVotes;
  const nextRank    = usedVotes + 1;

  const resultData = results?.find(r => r.product?.id === prod.id);
  const maxScore   = results?.[0]?.score || 1;
  const pct        = resultData ? Math.round((resultData.score / maxScore) * 100) : 0;
  const rank       = results ? results.findIndex(r => r.product?.id === prod.id) + 1 : 0;

  const handleVote = async (explicitStars) => {
    setLoading(true);
    await onVote(prod.id, explicitStars ?? null);
    setPendingStars(0);
    setLoading(false);
  };
  const handleRemove = async () => { setLoading(true); await onRemove(prod.id); setLoading(false); };
  const handleModify = async (r = null) => { setLoading(true); await onModifyVote(prod.id, r); setLoading(false); };

  return (
    <motion.div
      layout
      whileHover={{ y: -3, transition: { duration: 0.18 } }}
      className={`rounded-2xl overflow-hidden flex flex-col transition-all duration-300 ${
        isVoted
          ? "border border-amber-400/50 shadow-lg shadow-amber-400/10"
          : "border border-white/6 hover:border-white/12"
      }`}
      style={{ background: isVoted ? "linear-gradient(145deg,#181200,#1a1400)" : "#111111" }}
    >
      {/* Imagen */}
      <div className="relative overflow-hidden" style={{ aspectRatio: "4/3" }}>
        {prod.images?.[0]
          ? <img src={imgUrl(prod.images[0])} alt={prod.title} className="w-full h-full object-cover transition-transform duration-500 hover:scale-105" />
          : <div className="w-full h-full bg-white/4 flex items-center justify-center"><Store size={32} className="text-white/20" /></div>
        }
        {/* Overlay sutil */}
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />

        {/* Badge votado */}
        {isVoted && (
          <motion.div
            initial={{ scale: 0.5, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ type: "spring", stiffness: 300, damping: 20 }}
            className="absolute top-2 left-2 bg-amber-400 text-black text-[10px] font-black px-2 py-1 rounded-lg flex items-center gap-1 shadow-lg"
          >
            {votingType === "stars"
              ? <><Star size={10} fill="black" stroke="black" /> {voteForThis.voteRank}★</>
              : <><span className="text-xs leading-none">{MEDALS[voteForThis.voteRank - 1]}</span> {RANK_LABELS[voteForThis.voteRank - 1]}</>
            }
          </motion.div>
        )}

        {/* Rank en resultados */}
        {showResults && rank > 0 && (
          <div className="absolute top-2 right-2 bg-black/70 backdrop-blur-sm text-white text-xs font-bold px-2 py-1 rounded-full">
            {MEDALS[rank - 1]} #{rank}
          </div>
        )}

        {/* Nombre del producto sobre imagen */}
        <div className="absolute bottom-0 left-0 right-0 p-2.5">
          <h4 className="font-bold text-white text-xs sm:text-sm leading-tight line-clamp-2 drop-shadow-md">{prod.title}</h4>
        </div>
      </div>

      <div className="p-3 flex flex-col flex-1">
        <p className="text-amber-400 font-bold text-sm mb-2">
          ${Number(prod.price || 0).toLocaleString("es-CO")}
        </p>

        {/* Barra de resultados */}
        {showResults && resultData && (
          <div className="mb-3">
            <div className="flex justify-between text-[10px] text-white/40 mb-1">
              <span>{resultData.votes} votos</span>
              <span className="font-bold text-amber-400">{resultData.score} pts</span>
            </div>
            <div className="w-full bg-white/5 rounded-full h-1.5">
              <motion.div
                initial={{ width: 0 }}
                animate={{ width: `${pct}%` }}
                transition={{ duration: 0.6, ease: "easeOut" }}
                className="h-1.5 rounded-full bg-gradient-to-r from-amber-400 to-amber-500"
              />
            </div>
          </div>
        )}

        {/* Botones de acción */}
        <div className="mt-auto">
          {votingClosed ? (
            <div className="flex items-center justify-center gap-1.5 py-2 rounded-xl text-xs font-semibold bg-white/4 text-white/25 border border-white/6 cursor-not-allowed">
              <Lock size={11} /> {isVoted ? "Voto registrado" : "Votación cerrada"}
            </div>
          ) : isVoted ? (
            votingType === "stars" && canModifyVote ? (
              <div className="flex flex-col gap-2">
                <div className="flex flex-col items-center gap-1 bg-amber-400/8 rounded-xl py-2 px-1 border border-amber-400/15">
                  <p className="text-[9px] text-amber-400/70 font-semibold uppercase tracking-wider">Toca para cambiar</p>
                  <StarRating value={voteForThis.voteRank} onChange={handleModify} disabled={loading} />
                </div>
                {canRemoveVote && (
                  <button onClick={handleRemove} disabled={loading}
                    className="flex items-center justify-center gap-1 py-1.5 rounded-xl text-xs font-semibold border border-red-500/20 text-red-400/70 hover:bg-red-500/8 transition">
                    <X size={11} /> Quitar voto
                  </button>
                )}
              </div>
            ) : votingType === "stars" ? (
              <div className="flex flex-col gap-2">
                <div className="flex flex-col items-center gap-1 bg-amber-400/8 rounded-xl py-2 border border-amber-400/15">
                  <p className="text-[9px] text-amber-400/70 font-semibold uppercase tracking-wider">Tu calificación</p>
                  <StarRating value={voteForThis.voteRank} onChange={() => {}} disabled />
                </div>
                {canRemoveVote ? (
                  <button onClick={handleRemove} disabled={loading}
                    className="flex items-center justify-center gap-1 py-1.5 rounded-xl text-xs border border-red-500/20 text-red-400/70 hover:bg-red-500/8 transition">
                    {loading ? <Loader2 size={11} className="animate-spin" /> : <X size={11} />} Quitar
                  </button>
                ) : (
                  <div className="flex items-center justify-center gap-1 py-1.5 rounded-xl text-xs font-semibold bg-amber-400/10 text-amber-400 border border-amber-400/20">
                    <CheckCircle2 size={11} /> ¡Calificado!
                  </div>
                )}
              </div>
            ) : canModifyVote ? (
              <div className="flex gap-2">
                <button onClick={() => handleModify()} disabled={loading}
                  className="flex-1 flex items-center justify-center gap-1 py-2 rounded-xl text-xs font-semibold border border-white/10 text-white/50 hover:bg-white/5 transition">
                  {loading ? <Loader2 size={12} className="animate-spin" /> : <Pencil size={11} />} Cambiar
                </button>
                {canRemoveVote && (
                  <button onClick={handleRemove} disabled={loading}
                    className="flex items-center justify-center px-2.5 py-2 rounded-xl border border-red-500/20 text-red-400/70 hover:bg-red-500/8 transition">
                    <X size={12} />
                  </button>
                )}
              </div>
            ) : canRemoveVote ? (
              <button onClick={handleRemove} disabled={loading}
                className="w-full flex items-center justify-center gap-1 py-2 rounded-xl text-xs border border-red-500/20 text-red-400/70 hover:bg-red-500/8 transition">
                {loading ? <Loader2 size={12} className="animate-spin" /> : <X size={12} />} Quitar voto
              </button>
            ) : (
              <div className="flex items-center justify-center gap-1 py-2 rounded-xl text-xs font-semibold bg-amber-400/10 text-amber-400 border border-amber-400/20">
                <CheckCircle2 size={12} /> ¡Votaste aquí!
              </div>
            )
          ) : canVote ? (
            votingType === "stars" ? (
              <div className="flex flex-col gap-2">
                <p className="text-[9px] text-white/30 font-semibold uppercase tracking-wider text-center">
                  {pendingStars === 0 ? "Toca para calificar" : `${pendingStars} estrella${pendingStars !== 1 ? "s" : ""}`}
                </p>
                <div className="flex justify-center">
                  <StarRating value={pendingStars} onChange={setPendingStars} disabled={loading} />
                </div>
                <AnimatePresence>
                  {pendingStars > 0 && (
                    <motion.button
                      key="confirm"
                      initial={{ opacity: 0, y: 4 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 4 }}
                      onClick={() => handleVote(pendingStars)} disabled={loading}
                      whileTap={{ scale: 0.93 }}
                      className="w-full flex items-center justify-center gap-2 py-2.5 rounded-xl text-xs font-black bg-amber-400 text-black shadow-lg shadow-amber-400/25 disabled:opacity-50"
                    >
                      {loading ? <Loader2 size={12} className="animate-spin" /> : <Star size={11} fill="black" stroke="black" />}
                      Confirmar
                    </motion.button>
                  )}
                </AnimatePresence>
              </div>
            ) : (
              <motion.button
                onClick={() => handleVote()} disabled={loading}
                whileTap={{ scale: 0.93 }}
                className="relative w-full flex items-center justify-center gap-2 py-2.5 rounded-xl text-xs font-black bg-amber-400 text-black shadow-lg shadow-amber-400/20 overflow-hidden disabled:opacity-50"
              >
                <motion.span
                  className="absolute inset-0 bg-white/20 rounded-xl"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: [0, 0.3, 0] }}
                  transition={{ duration: 2, repeat: Infinity }}
                />
                {loading
                  ? <Loader2 size={13} className="animate-spin" />
                  : <span className="text-sm leading-none">{MEDALS[nextRank - 1]}</span>
                }
                <span>{maxVotes > 1 ? `${RANK_LABELS[nextRank - 1]} voto` : (voteButtonLabel || "Votar")}</span>
              </motion.button>
            )
          ) : (
            <div className="py-2 rounded-xl text-[10px] text-center text-white/25 bg-white/4 border border-white/6 font-semibold tracking-wide">
              {usedVotes >= maxVotes ? "Votos agotados" : "Votaste aquí"}
            </div>
          )}
        </div>
      </div>
    </motion.div>
  );
}

/* ─── Botón enviar T&C ────────────────────────────────────────── */
function SendTermsButton({ eventId }) {
  const [sending, setSending] = useState(false);
  const [sent, setSent]       = useState(false);

  const handleSend = async () => {
    if (sending || sent) return;
    setSending(true);
    try {
      const res = await API.post(`/events/${eventId}/send-terms`);
      toast.success(`📋 ${res.data?.message ?? "T&C enviados"}`);
      setSent(true);
      setTimeout(() => setSent(false), 10000);
    } catch (err) {
      toast.error(err.response?.data?.message ?? "Error al enviar T&C");
    } finally { setSending(false); }
  };

  return (
    <button onClick={handleSend} disabled={sending}
      className={`flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-bold border transition ${
        sent
          ? "border-green-500/30 bg-green-500/10 text-green-400"
          : "border-white/10 bg-white/5 text-white/60 hover:bg-white/8"
      }`}
    >
      {sending ? <Loader2 size={14} className="animate-spin" /> : sent ? <CheckCircle2 size={14} /> : <span>📋</span>}
      {sending ? "Enviando…" : sent ? "T&C enviados" : "Enviar T&C a participantes"}
    </button>
  );
}

/* ─── Podio de resultados ─────────────────────────────────────── */
function ResultsPodium({ results, totalVotes, totalScore, votingPoints }) {
  const hasPoints = Array.isArray(votingPoints) && votingPoints.length > 1;
  const maxScore  = results[0]?.score || 1;

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="rounded-2xl border border-white/8 overflow-hidden mb-6"
      style={{ background: "#111111" }}
    >
      {/* Header */}
      <div className="px-5 py-4 border-b border-white/6 flex items-center gap-3">
        <div className="w-9 h-9 rounded-xl bg-amber-400/10 border border-amber-400/20 flex items-center justify-center">
          <Trophy size={18} className="text-amber-400" />
        </div>
        <div>
          <h3 className="font-bold text-white text-base leading-none">Resultados</h3>
          <p className="text-[11px] text-white/35 mt-0.5">Clasificación final</p>
        </div>
        <div className="ml-auto flex items-center gap-4 text-xs text-white/35">
          <span className="flex items-center gap-1.5"><Users size={12} /> {totalVotes} votos</span>
          {hasPoints && <span className="flex items-center gap-1.5"><Trophy size={12} /> {totalScore} pts</span>}
        </div>
      </div>

      <div className="p-5 space-y-3">
        {results.map((r, idx) => {
          const pct = Math.round((r.score / maxScore) * 100);
          const medalColors = ["from-amber-400 to-yellow-500", "from-zinc-300 to-zinc-400", "from-orange-400 to-amber-500"];
          return (
            <div key={r.product?.id ?? idx} className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-xl flex items-center justify-center shrink-0"
                style={{
                  background: idx === 0 ? "rgba(251,191,36,0.12)" : idx === 1 ? "rgba(209,213,219,0.08)" : idx === 2 ? "rgba(251,146,60,0.10)" : "rgba(255,255,255,0.04)",
                  border: idx === 0 ? "1px solid rgba(251,191,36,0.25)" : "1px solid rgba(255,255,255,0.06)",
                }}>
                <span className="text-lg leading-none">{MEDALS[idx] ?? `${idx + 1}`}</span>
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between mb-1.5">
                  <p className="text-sm font-semibold text-white truncate leading-none">{r.product?.title}</p>
                  <div className="flex items-center gap-2 shrink-0 ml-2">
                    <span className="text-[10px] text-white/30">{r.votes}v</span>
                    {hasPoints && (
                      <span className="text-[10px] font-bold text-amber-400 bg-amber-400/10 px-1.5 py-0.5 rounded-full">
                        {r.score}pts
                      </span>
                    )}
                  </div>
                </div>
                <div className="w-full bg-white/5 rounded-full h-1.5 overflow-hidden">
                  <motion.div
                    initial={{ width: 0 }}
                    animate={{ width: `${pct}%` }}
                    transition={{ duration: 0.7, delay: idx * 0.1, ease: "easeOut" }}
                    className={`h-1.5 rounded-full bg-gradient-to-r ${medalColors[idx] ?? "from-supernova-400 to-supernova-500"}`}
                  />
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </motion.div>
  );
}

/* ─── Componente de patrocinador individual ───────────────────── */
function SponsorItem({ s, size = "md" }) {
  const logo = imgUrl(s.sponsorLogoBase64 ?? s.company?.logo);
  const name = s.displayName ?? s.company?.name;

  const sizes = {
    lg: { img: "w-20 h-20 sm:w-24 sm:h-24", ring: "ring-2 ring-amber-400/40", fallback: 28, text: "text-xs font-semibold text-white/70 max-w-[96px]" },
    md: { img: "w-14 h-14 sm:w-16 sm:h-16", ring: "ring-1 ring-white/15",     fallback: 20, text: "text-[10px] text-white/50 max-w-[64px]" },
    sm: { img: "w-10 h-10 sm:w-11 sm:h-11", ring: "ring-1 ring-white/8",      fallback: 15, text: "text-[9px] text-white/35 max-w-[44px]" },
  };
  const s_ = sizes[size];

  const inner = (
    <div className="flex flex-col items-center gap-1.5">
      {logo
        ? <img src={logo} alt={name} className={`${s_.img} rounded-2xl object-cover ${s_.ring} shadow-lg`} />
        : <div className={`${s_.img} rounded-2xl bg-white/4 border border-white/8 flex items-center justify-center`}>
            <Store size={s_.fallback} className="text-white/20" />
          </div>
      }
      {name && <span className={`${s_.text} text-center truncate leading-tight`}>{name}</span>}
    </div>
  );

  return s.websiteUrl
    ? <a href={s.websiteUrl} target="_blank" rel="noopener noreferrer" className="hover:opacity-80 transition-opacity">{inner}</a>
    : inner;
}

/* ══════════════════════════════════════════════════════════════
   Página principal
   ══════════════════════════════════════════════════════════════ */
export default function EventPage() {
  const { id } = useParams();
  const navigate = useNavigate();

  const currentUser = (() => { try { return JSON.parse(localStorage.getItem("user") || "null"); } catch { return null; } })();

  const [event,      setEvent]      = useState(null);
  const [myVotes,    setMyVotes]    = useState([]);
  const [results,    setResults]    = useState(null);
  const [loading,    setLoading]    = useState(true);
  const [joining,    setJoining]    = useState(false);
  const [joinStatus, setJoinStatus] = useState(null);

  const loadMyVotes = useCallback(async (eventId) => {
    try {
      const res = await API.get(`/votes/${eventId}/my-votes`);
      setMyVotes(res.data?.data?.votes ?? []);
    } catch { /* sin autenticar */ }
  }, []);

  const loadResults = useCallback(async (eventId) => {
    try {
      const res = await API.get(`/votes/${eventId}/results`);
      setResults(res.data?.data ?? null);
    } catch { /* no publicados */ }
  }, []);

  const loadEvent = useCallback(async () => {
    try {
      const res = await API.get(`/events/${id}`);
      const ev  = res.data?.data?.event ?? null;
      setEvent(ev);
      if (ev?.id) {
        await Promise.all([loadMyVotes(ev.id), loadResults(ev.id)]);
        if (currentUser?.role === "company" && ev.sponsors) {
          const found = ev.sponsors.find(s => s.company?.id === currentUser?.companyId || s.companyId === currentUser?.companyId);
          if (found) setJoinStatus(found.status ?? "approved");
        }
      }
    } catch { toast.error("Error cargando evento"); }
    finally { setLoading(false); }
  }, [id, loadMyVotes, loadResults, currentUser?.role, currentUser?.companyId]);

  useEffect(() => { loadEvent(); }, [loadEvent]);

  const handleJoin = async () => {
    setJoining(true);
    try {
      const res = await API.post(`/events/${id}/join`);
      const status = res.data?.data?.sponsor?.status ?? "approved";
      setJoinStatus(status);
      toast.success(res.data?.message || (status === "approved" ? "¡Te registraste!" : "Solicitud enviada"));
      loadEvent();
    } catch (err) {
      toast.error(err.response?.data?.message || "No se pudo unirse");
    } finally { setJoining(false); }
  };

  const handleVote = async (productId, rank = null) => {
    try {
      const payload = { eventId: event.id, productId };
      if (rank !== null) payload.voteRank = rank;
      if (event.requireDeviceId === true) {
        const did = await getDeviceId();
        if (did) payload.deviceId = did;
      }
      if (event.requireLocation) {
        toast.info("Verificando tu ubicación…", { autoClose: 3000 });
        const pos = await getUserLocation();
        if (!pos) { toast.error("No se pudo obtener tu ubicación."); return; }
        payload.userLat = pos.lat;
        payload.userLng = pos.lng;
      }
      await API.post("/votes", payload);
      toast.success(rank !== null ? "⭐ Calificación actualizada" : "¡Voto registrado! 🎉");
      await Promise.all([loadMyVotes(event.id), loadResults(event.id)]);
    } catch (err) {
      toast.error(err.response?.data?.message || err.response?.data?.error || "No se pudo votar");
    }
  };

  const handleRemove = async (productId) => {
    try {
      await API.delete(`/votes/${event.id}/${productId}`);
      toast.info("Voto eliminado");
      await Promise.all([loadMyVotes(event.id), loadResults(event.id)]);
    } catch { toast.error("No se pudo eliminar el voto"); }
  };

  const handleModifyVote = async (productId, rank = null) => {
    if (event.votingType === "stars" && rank !== null) {
      await handleVote(productId, rank);
    } else {
      try {
        await API.delete(`/votes/${event.id}/${productId}`);
        toast.info("Voto listo para cambiar — elige de nuevo");
        await Promise.all([loadMyVotes(event.id), loadResults(event.id)]);
      } catch { toast.error("No se pudo cambiar el voto"); }
    }
  };

  /* Loading */
  if (loading) return (
    <div className="min-h-screen bg-[#080808] flex items-center justify-center">
      <div className="flex flex-col items-center gap-4">
        <div className="w-16 h-16 rounded-2xl bg-amber-400/10 border border-amber-400/20 flex items-center justify-center">
          <Loader2 size={28} className="animate-spin text-amber-400" />
        </div>
        <p className="text-white/30 text-sm font-medium">Cargando evento…</p>
      </div>
    </div>
  );

  if (!event) return (
    <div className="min-h-screen bg-[#080808] flex items-center justify-center">
      <p className="text-white/30 text-sm">Evento no encontrado.</p>
    </div>
  );

  /* Plantilla BurgerFest */
  if (event.templateId === "burgerfest") {
    return (
      <Suspense fallback={
        <div className="min-h-screen bg-black flex items-center justify-center">
          <Loader2 size={40} className="animate-spin text-orange-500" />
        </div>
      }>
        <BurgerFestEventPage
          event={event} myVotes={myVotes} results={results}
          currentUser={currentUser} joinStatus={joinStatus}
          onVote={handleVote} onRemove={handleRemove} onJoin={handleJoin}
          joining={joining} reload={loadEvent}
        />
      </Suspense>
    );
  }

  /* Plantilla DotBurgerFest */
  if (event.templateId === "dotburgerfest") {
    return (
      <Suspense fallback={
        <div className="min-h-screen bg-black flex items-center justify-center">
          <Loader2 size={40} className="animate-spin" style={{ color: "#FFD600" }} />
        </div>
      }>
        <DotBurgerFestPage
          event={event} myVotes={myVotes} results={results}
          currentUser={currentUser} joinStatus={joinStatus}
          onVote={handleVote} onRemove={handleRemove} onJoin={handleJoin}
          joining={joining} reload={loadEvent}
        />
      </Suspense>
    );
  }

  /* ── Derivados ──────────────────────────────────────────────── */
  const maxVotes     = event.maxVotesPerDevice ?? 1;
  const usedVotes    = myVotes.length;
  const votingClosed = Boolean(event.votingClosed);
  const isActive     = event.active && new Date() <= new Date(event.endDate);
  const showResults  = Boolean(results?.results?.length);
  const allowRemove  = event.canRemoveVote !== false;
  const allowModify  = Boolean(event.canModifyVote);

  const sortedSponsors = [...(event.sponsors ?? [])].sort((a, b) => (a.displayOrder ?? 0) - (b.displayOrder ?? 0));
  const goldSponsors   = sortedSponsors.filter(s => s.membershipTier === "gold");
  const silverSponsors = sortedSponsors.filter(s => s.membershipTier === "silver");
  const bronzeSponsors = sortedSponsors.filter(s => !s.membershipTier || s.membershipTier === "bronze");
  const hasSponsorTiers = goldSponsors.length > 0 || silverSponsors.length > 0 || bronzeSponsors.length > 0;

  const bgStyle = (() => {
    if (event.backgroundType === "gradient") {
      return { background: `linear-gradient(${event.backgroundGradientAngle ?? 135}deg, ${event.backgroundGradientFrom ?? "#080808"}, ${event.backgroundGradientTo ?? "#1a0a00"})` };
    }
    if (event.backgroundType === "image" && event.backgroundImageBase64) {
      return { backgroundImage: `url(${imgUrl(event.backgroundImageBase64)})`, backgroundSize: event.backgroundImageSize ?? "cover", backgroundPosition: "center", backgroundRepeat: "no-repeat", backgroundAttachment: "fixed" };
    }
    if (event.backgroundType === "color" && event.backgroundColor) {
      return { backgroundColor: event.backgroundColor };
    }
    return {};
  })();

  const titleStyle = {
    ...(event.titleColor    ? { color: event.titleColor }                   : {}),
    ...(event.titleFont     ? { fontFamily: FONT_FAMILIES[event.titleFont] } : {}),
    ...(event.titleFontSize ? { fontSize: FONT_SIZES[event.titleFontSize] }  : {}),
    ...(event.titleAlign    ? { textAlign: event.titleAlign }               : {}),
  };

  const rawProducts = event.sponsors?.flatMap(s =>
    (s.products ?? []).map(p => ({ ...(p.product ?? p), companyName: s.company?.name }))
  ) ?? [];

  const allProducts = showResults
    ? [...rawProducts].sort((a, b) => {
        const ra = results.results.findIndex(r => r.product?.id === a.id);
        const rb = results.results.findIndex(r => r.product?.id === b.id);
        return (ra === -1 ? 999 : ra) - (rb === -1 ? 999 : rb);
      })
    : rawProducts;

  /* ── Render ─────────────────────────────────────────────────── */
  return (
    <div className="min-h-screen pb-28" style={{ backgroundColor: "#080808", ...bgStyle }}>

      {/* ══ HERO ══════════════════════════════════════════════════ */}
      <div className="relative w-full">
        {(event.banner || event.bannerBase64) ? (
          <img
            src={imgUrl(event.banner || event.bannerBase64)}
            alt={event.title}
            className="w-full object-cover"
            style={{ height: "clamp(200px, 40vw, 360px)" }}
          />
        ) : (
          <div
            className="w-full"
            style={{ height: "clamp(160px, 30vw, 240px)", background: "linear-gradient(135deg, #1a0e00 0%, #0d0d0d 100%)" }}
          />
        )}

        {/* Gradiente de fade a negro */}
        <div className="absolute inset-0 bg-gradient-to-t from-[#080808] via-[#080808]/30 to-transparent" />

        {/* Botón volver */}
        <button
          onClick={() => navigate(-1)}
          className="absolute top-4 left-4 w-9 h-9 flex items-center justify-center rounded-xl bg-black/50 backdrop-blur-md border border-white/10 text-white hover:bg-black/70 transition"
        >
          <ArrowLeft size={18} />
        </button>

        {/* Logo del evento — flotando sobre el hero */}
        {event.eventLogoBase64 && (
          <div className="absolute bottom-0 left-1/2 -translate-x-1/2 translate-y-1/2">
            <div className="relative">
              {/* Glow ring */}
              <div className="absolute inset-0 rounded-2xl bg-amber-400/20 blur-xl scale-110" />
              <div className="relative w-20 h-20 sm:w-24 sm:h-24 rounded-2xl overflow-hidden border-2 border-amber-400/30 shadow-2xl shadow-black/80"
                style={{ background: "#111" }}>
                <img src={imgUrl(event.eventLogoBase64)} alt={event.title} className="w-full h-full object-contain p-1" />
              </div>
            </div>
          </div>
        )}
      </div>

      {/* ══ CONTENIDO ═════════════════════════════════════════════ */}
      <div className="max-w-4xl mx-auto px-4">

        {/* Espacio para el logo flotante */}
        {event.eventLogoBase64 && <div className="h-12 sm:h-14" />}

        {/* ── Título e info ──────────────────────────────────────── */}
        <div className="text-center mt-4 mb-6">
          <h1
            className="font-black text-white text-2xl sm:text-3xl md:text-4xl leading-tight"
            style={titleStyle}
          >
            {event.title}
          </h1>

          {event.description && (
            <p className="text-white/45 mt-2 text-sm sm:text-base leading-relaxed max-w-xl mx-auto">
              {event.description}
            </p>
          )}

          {/* Fechas + estado */}
          <div className="flex flex-wrap items-center justify-center gap-2 mt-4">
            <span className="flex items-center gap-1.5 text-xs text-white/40 bg-white/5 border border-white/8 px-3 py-1.5 rounded-full">
              <Calendar size={12} className="text-amber-400/60" />
              {event.startDate?.substring(0, 10)}
            </span>
            <span className="text-white/15 text-xs">→</span>
            <span className="flex items-center gap-1.5 text-xs text-white/40 bg-white/5 border border-white/8 px-3 py-1.5 rounded-full">
              <CalendarDays size={12} className="text-amber-400/60" />
              {event.endDate?.substring(0, 10)}
            </span>

            {!isActive && (
              <span className="text-xs font-bold bg-red-500/15 text-red-400 border border-red-500/20 px-3 py-1.5 rounded-full">
                Evento finalizado
              </span>
            )}
            {isActive && votingClosed && (
              <span className="flex items-center gap-1 text-xs font-bold bg-white/5 text-white/35 border border-white/8 px-3 py-1.5 rounded-full">
                <Lock size={10} /> Votación cerrada
              </span>
            )}
          </div>
        </div>

        {/* ── Requisitos de votación ────────────────────────────── */}
        {isActive && (event.requireLocation || event.requireDeviceId) && (
          <div className="flex flex-wrap justify-center gap-2 mb-6">
            {event.requireLocation && (
              <span className="flex items-center gap-1.5 text-xs bg-emerald-500/10 text-emerald-400 border border-emerald-500/20 px-3 py-1.5 rounded-full font-medium">
                <MapPin size={11} /> Debes estar en el evento para votar
              </span>
            )}
            {event.requireDeviceId === true && (
              <span className="flex items-center gap-1.5 text-xs bg-blue-500/10 text-blue-400 border border-blue-500/20 px-3 py-1.5 rounded-full font-medium">
                <Smartphone size={11} /> 1 dispositivo, 1 voto
              </span>
            )}
          </div>
        )}

        {/* ── Estado de votación (banner) ───────────────────────── */}
        {isActive && !votingClosed && (
          <motion.div
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            className="rounded-2xl border mb-6 overflow-hidden"
            style={{
              background: usedVotes >= maxVotes
                ? "linear-gradient(135deg, rgba(251,191,36,0.08), rgba(245,158,11,0.05))"
                : "rgba(255,255,255,0.025)",
              borderColor: usedVotes >= maxVotes ? "rgba(251,191,36,0.2)" : "rgba(255,255,255,0.06)",
            }}
          >
            <div className="px-5 py-4">
              <div className="flex items-start gap-3">
                <span className="text-2xl leading-none mt-0.5">
                  {usedVotes >= maxVotes ? "✅" : "🗳️"}
                </span>
                <div className="flex-1">
                  <p className={`text-sm font-bold ${usedVotes >= maxVotes ? "text-amber-400" : "text-white/80"}`}>
                    {maxVotes === 1
                      ? (usedVotes === 0 ? "Tienes 1 voto — elige tu favorito" : "Ya votaste — puedes cambiar hasta el cierre")
                      : (usedVotes === 0 ? `Tienes ${maxVotes} votos disponibles`
                        : usedVotes < maxVotes ? `Llevas ${usedVotes} de ${maxVotes} votos`
                        : `¡Usaste todos tus ${maxVotes} votos!`)}
                  </p>

                  {/* Info de puntos / estrellas */}
                  {event.votingType === "stars" ? (
                    <p className="text-xs text-white/30 mt-1 flex items-center gap-1">
                      <Star size={11} className="text-amber-400/60" fill="currentColor" />
                      Califica del 1 al 5 estrellas
                    </p>
                  ) : maxVotes > 1 && Array.isArray(event.votingPoints) && (
                    <div className="flex flex-wrap gap-3 mt-2">
                      {event.votingPoints.map((pts, i) => (
                        <span key={i} className="text-[10px] text-white/30">
                          {MEDALS[i]} <span className="font-semibold text-white/50">{pts}pts</span>
                        </span>
                      ))}
                    </div>
                  )}

                  <div className="mt-3">
                    <VoteProgress used={usedVotes} max={maxVotes} />
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        )}

        {/* ── Botón unirse (solo empresas) ──────────────────────── */}
        {isActive && event.isOpenEvent !== undefined && currentUser?.role === "company" && (
          <div className="mb-6">
            {joinStatus === "approved" ? (
              <div className="flex items-center justify-center gap-2 text-sm font-semibold text-emerald-400 bg-emerald-500/10 border border-emerald-500/20 px-4 py-3 rounded-2xl">
                <CheckCircle2 size={16} /> Tu empresa ya participa en este evento
              </div>
            ) : joinStatus === "pending" ? (
              <div className="flex items-center justify-center gap-2 text-sm font-semibold text-amber-400 bg-amber-400/8 border border-amber-400/20 px-4 py-3 rounded-2xl">
                <Clock size={16} /> Solicitud pendiente de aprobación
              </div>
            ) : (
              <button onClick={handleJoin} disabled={joining}
                className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-2xl text-sm font-bold transition disabled:opacity-60"
                style={{ background: event.isOpenEvent ? "#fbbf24" : "#1a1a1a", color: event.isOpenEvent ? "#000" : "#fff", border: event.isOpenEvent ? "none" : "1px solid rgba(255,255,255,0.1)" }}
              >
                {joining ? <Loader2 size={16} className="animate-spin" /> : <Store size={16} />}
                {joining ? "Procesando..." : event.isOpenEvent ? "Participar con mi empresa" : "Solicitar participar"}
              </button>
            )}
          </div>
        )}

        {/* ══ PATROCINADORES ════════════════════════════════════════ */}
        {event.showSponsorsSection && hasSponsorTiers && (
          <div className="mb-8">
            {/* Separador visual */}
            <div className="flex items-center gap-4 mb-6">
              <div className="h-px flex-1 bg-gradient-to-r from-transparent to-white/8" />
              <span className="text-[10px] font-bold uppercase tracking-[0.25em] text-white/25">
                Patrocinadores
              </span>
              <div className="h-px flex-1 bg-gradient-to-l from-transparent to-white/8" />
            </div>

            <div className="space-y-8">
              {/* Tier Oro */}
              {goldSponsors.length > 0 && (
                <div>
                  <div className="flex items-center gap-2 justify-center mb-4">
                    <span className="text-sm">🥇</span>
                    <span className="text-[9px] font-black uppercase tracking-[0.3em] text-amber-400/50">
                      Patrocinador Oro
                    </span>
                  </div>
                  <div className="flex flex-wrap gap-6 justify-center">
                    {goldSponsors.map(s => <SponsorItem key={s.id ?? s.companyId} s={s} size="lg" />)}
                  </div>
                </div>
              )}

              {/* Tier Plata */}
              {silverSponsors.length > 0 && (
                <div>
                  <div className="flex items-center gap-2 justify-center mb-3">
                    <span className="text-xs">🥈</span>
                    <span className="text-[9px] font-black uppercase tracking-[0.3em] text-white/20">
                      Patrocinador Plata
                    </span>
                  </div>
                  <div className="flex flex-wrap gap-4 justify-center">
                    {silverSponsors.map(s => <SponsorItem key={s.id ?? s.companyId} s={s} size="md" />)}
                  </div>
                </div>
              )}

              {/* Tier Bronce */}
              {bronzeSponsors.length > 0 && (
                <div>
                  <div className="flex items-center gap-2 justify-center mb-3">
                    <span className="text-xs">🥉</span>
                    <span className="text-[9px] font-black uppercase tracking-[0.3em] text-white/15">
                      Participantes
                    </span>
                  </div>
                  <div className="flex flex-wrap gap-3 justify-center">
                    {bronzeSponsors.map(s => <SponsorItem key={s.id ?? s.companyId} s={s} size="sm" />)}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* ══ RESULTADOS (podio) ════════════════════════════════════ */}
        {showResults && (
          <>
            <ResultsPodium
              results={results.results}
              totalVotes={results.totalVotes}
              totalScore={results.totalScore}
              votingPoints={event.votingPoints}
            />
            {currentUser?.role === "admin" && (
              <div className="flex flex-wrap justify-end gap-2 mb-6">
                {event.termsAndConditions && <SendTermsButton eventId={id} />}
                <button
                  onClick={() => navigate(`/event/${id}/ceremony`)}
                  className="flex items-center gap-2 px-4 py-2 bg-amber-400 hover:bg-amber-300 text-black font-bold rounded-xl text-sm shadow-lg shadow-amber-400/20 transition"
                >
                  🎬 Presentación de resultados
                </button>
              </div>
            )}
          </>
        )}

        {/* ══ PRODUCTOS ═════════════════════════════════════════════ */}
        {allProducts.length > 0 ? (
          <>
            {/* Header de sección */}
            <div className="flex items-center gap-3 mb-5">
              <div className="h-px flex-1 bg-white/6" />
              <div className="flex items-center gap-2">
                {showResults
                  ? <><BarChart2 size={15} className="text-amber-400" /><span className="text-sm font-bold text-white/60">Productos y resultados</span></>
                  : <><span className="text-base">✨</span><span className="text-sm font-bold text-white/60">Elige tu favorito</span></>
                }
              </div>
              <div className="h-px flex-1 bg-white/6" />
            </div>

            <AnimatePresence>
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3"
              >
                {allProducts.map((prod, i) => (
                  <motion.div
                    key={prod.id}
                    initial={{ opacity: 0, y: 16 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: i * 0.04, duration: 0.3 }}
                  >
                    <ProductCard
                      prod={prod}
                      myVotes={myVotes}
                      maxVotes={maxVotes}
                      onVote={isActive && !votingClosed ? handleVote : () => toast.info(votingClosed ? "La votación está cerrada" : "El evento ha finalizado")}
                      onRemove={isActive && !votingClosed ? handleRemove : () => {}}
                      onModifyVote={isActive && !votingClosed ? handleModifyVote : () => {}}
                      results={results?.results ?? null}
                      showResults={showResults}
                      canRemoveVote={allowRemove}
                      canModifyVote={allowModify}
                      votingType={event.votingType ?? "simple"}
                      voteButtonLabel={event.voteButtonLabel}
                      votingClosed={votingClosed}
                    />
                  </motion.div>
                ))}
              </motion.div>
            </AnimatePresence>
          </>
        ) : (
          <div className="flex flex-col items-center justify-center py-20 text-center">
            <div className="w-16 h-16 rounded-2xl bg-white/3 border border-white/6 flex items-center justify-center mb-4">
              <Store size={28} className="text-white/15" />
            </div>
            <p className="text-white/25 text-sm">No hay productos en este evento.</p>
          </div>
        )}
      </div>
    </div>
  );
}
